import React from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../ui/form'

// Password validation schema that can be reused
export const passwordValidationSchema = z.string()
  .min(8, 'Password must be at least 8 characters')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number')
  .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character')

// Password requirements component that can be reused
export const PasswordRequirements: React.FC = () => (
  <div className="text-sm text-[#CECFD2]">
    Password must:
    <ul className="list-disc pl-5 mt-1">
      <li>Be at least 8 characters long</li>
      <li>Contain at least one uppercase letter</li>
      <li>Contain at least one lowercase letter</li>
      <li>Contain at least one number</li>
      <li>Contain at least one special character</li>
    </ul>
  </div>
)

// Props for the reusable password form
interface PasswordFormProps {
  showCurrentPassword?: boolean;
  onSubmit: (data: any) => Promise<void>;
  isLoading: boolean;
  submitButtonText: string;
  loadingButtonText: string;
}

export function PasswordForm({
  showCurrentPassword = false,
  onSubmit,
  isLoading,
  submitButtonText,
  loadingButtonText
}: PasswordFormProps) {
  // Create the form schema based on whether we need the current password field
  const formSchema = showCurrentPassword
    ? z.object({
        currentPassword: z.string().min(1, 'Current password is required'),
        newPassword: passwordValidationSchema,
        confirmPassword: z.string().min(1, 'Please confirm your password'),
      }).refine(data => data.newPassword === data.confirmPassword, {
        message: 'Passwords do not match',
        path: ['confirmPassword'],
      })
    : z.object({
        newPassword: passwordValidationSchema,
        confirmPassword: z.string().min(1, 'Please confirm your password'),
      }).refine(data => data.newPassword === data.confirmPassword, {
        message: 'Passwords do not match',
        path: ['confirmPassword'],
      });

  type FormValues = z.infer<typeof formSchema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: showCurrentPassword
      ? {
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        }
      : {
          newPassword: '',
          confirmPassword: '',
        },
  });

  const formIsValid = form.formState.isValid;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {showCurrentPassword && (
          <FormField
            control={form.control}
            name="currentPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[#CECFD2] font-semibold">Current Password</FormLabel>
                <FormControl>
                  <Input 
                    type="password" 
                    placeholder="Current password" 
                    {...field} 
                    disabled={isLoading} 
                    className="bg-[#26222E] border border-gray-700 text-[#CECFD2] placeholder:text-gray-500 h-12"
                  />
                </FormControl>
                <FormMessage className="text-red-400" />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="newPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-[#CECFD2] font-semibold">{showCurrentPassword ? 'New Password' : 'Password'}</FormLabel>
              <FormControl>
                <Input 
                  type="password" 
                  placeholder={showCurrentPassword ? 'New password' : 'Password'} 
                  {...field} 
                  disabled={isLoading} 
                  className="bg-[#26222E] border border-gray-700 text-[#CECFD2] placeholder:text-gray-500 h-12"
                />
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="confirmPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-[#CECFD2] font-semibold">Confirm {showCurrentPassword ? 'New ' : ''}Password</FormLabel>
              <FormControl>
                <Input 
                  type="password" 
                  placeholder={`Confirm ${showCurrentPassword ? 'new ' : ''}password`} 
                  {...field} 
                  disabled={isLoading} 
                  className="bg-[#26222E] border border-gray-700 text-[#CECFD2] placeholder:text-gray-500 h-12"
                />
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>
          )}
        />

        <PasswordRequirements />

        <Button 
          type="submit" 
          disabled={isLoading || !formIsValid} 
          className={`w-full font-semibold h-12 ${
            isLoading || !formIsValid 
              ? 'bg-[#26222E] opacity-50 cursor-not-allowed' 
              : 'bg-[#4E46FF] hover:bg-[#3d37cc]'
          } text-[#CECFD2]`}
        >
          {isLoading ? loadingButtonText : submitButtonText}
        </Button>
      </form>
    </Form>
  );
}